<template>
  <div class="wrapper">
    <container-component class="m-32">
      <div class="transfer has-centered-content">
        <client-img-component src="logo-wallbox-icon" class="logo-wallbox" />

        <h1 class="is-size-800 has-text-primary-500 mt-24" data-test-id="transferTitle">
          {{ i18n.t('mywb.transfer.title') }}
        </h1>

        <div class="mt-24 transfer-body">
          <wb-skeleton-loader
            v-if="current === 'loading'"
            type="dots"
            :loading="true"
            data-test-id="transferLoading"
          />

          <template v-else-if="current === 'loadError'">
            <p class="is-size-500" data-test-id="transferLoadError">
              {{ i18n.t('mywb.transfer.loadError') }}
            </p>
            <wb-button
              class="mt-24"
              size="block"
              data-test-id="transferRetry"
              :label="i18n.t('mywb.transfer.tryAgain')"
              @click="methods.load"
            />
          </template>

          <p
            v-else-if="unavailableKey"
            class="is-size-500"
            data-test-id="transferUnavailable"
          >
            {{ i18n.t(unavailableKey) }}
          </p>

          <template v-else-if="current === 'doneAccepted' && data.transfer">
            <p class="is-size-700 has-text-primary-500" data-test-id="transferAcceptedTitle">
              {{ i18n.t('mywb.transfer.acceptedTitle') }}
            </p>
            <p class="is-size-500 mt-16" data-test-id="transferAcceptedBody">
              {{ i18n.t('mywb.transfer.acceptedBody', { chargerSerial: data.transfer.chargerSerial }) }}
            </p>
          </template>

          <template v-else-if="current === 'doneRejected'">
            <p class="is-size-700 has-text-primary-500" data-test-id="transferRejectedTitle">
              {{ i18n.t('mywb.transfer.rejectedTitle') }}
            </p>
            <p class="is-size-500 mt-16" data-test-id="transferRejectedBody">
              {{ i18n.t('mywb.transfer.rejectedBody') }}
            </p>
          </template>

          <template v-else-if="data.transfer">
            <transfer-details
              :transfer="data.transfer"
              :show-expiry="true"
            />

            <template v-if="current === 'needsAccount'">
              <p class="is-size-400 mt-24" data-test-id="transferNeedAccount">
                {{ i18n.t('mywb.transfer.needAccount', { email: data.transfer.customerEmail }) }}
              </p>
              <wb-button
                class="mt-16"
                size="block"
                data-test-id="transferSignIn"
                :label="i18n.t('mywb.transfer.signIn')"
                @click="methods.goToAuth(ROUTES.AUTH_LOGIN)"
              />
              <wb-button
                class="mt-16"
                size="block"
                variant="white"
                outlined
                data-test-id="transferCreateAccount"
                :label="i18n.t('mywb.transfer.createAccount')"
                @click="methods.goToAuth(ROUTES.AUTH_REGISTER)"
              />
            </template>

            <template v-else-if="current === 'wrongAccount'">
              <p class="is-size-400 mt-24" data-test-id="transferWrongAccount">
                {{
                  i18n.t('mywb.transfer.wrongAccount', {
                    signedInEmail: data.sessionEmail,
                    email: data.transfer.customerEmail
                  })
                }}
              </p>
              <wb-button
                class="mt-16"
                size="block"
                data-test-id="transferSignOut"
                :loading="data.signingOut"
                :disabled="data.signingOut"
                :label="i18n.t('mywb.transfer.signOut')"
                @click="methods.signOut"
              />
            </template>

            <template v-else>
              <p
                v-if="data.actionError"
                class="is-size-400 has-text-danger-500 mt-24"
                data-test-id="transferActionError"
              >
                {{ i18n.t('mywb.transfer.actionError') }}
              </p>
              <wb-button
                class="mt-24"
                size="block"
                data-test-id="transferAccept"
                :disabled="data.working"
                :label="i18n.t('mywb.transfer.accept')"
                @click="methods.ask('accept')"
              />
              <wb-button
                class="mt-16"
                size="block"
                variant="white"
                outlined
                data-test-id="transferReject"
                :disabled="data.working"
                :label="i18n.t('mywb.transfer.reject')"
                @click="methods.ask('reject')"
              />
            </template>
          </template>
        </div>
      </div>
    </container-component>

    <wb-modal
      v-if="data.confirming && data.transfer"
      width="40"
      data-test-id="transferConfirm"
      @close="methods.cancel"
    >
      <p class="is-size-700 has-text-primary-500" data-test-id="transferConfirmTitle">
        {{
          data.confirming === 'accept'
            ? i18n.t('mywb.transfer.confirmAcceptTitle')
            : i18n.t('mywb.transfer.confirmRejectTitle')
        }}
      </p>
      <p class="is-size-500 mt-16" data-test-id="transferConfirmBody">
        {{
          data.confirming === 'accept'
            ? i18n.t('mywb.transfer.confirmAcceptBody', { chargerSerial: data.transfer.chargerSerial })
            : i18n.t('mywb.transfer.confirmRejectBody')
        }}
      </p>
      <template #actions>
        <wb-button
          size="block"
          data-test-id="transferConfirmButton"
          :disabled="data.working"
          :loading="data.working"
          :label="data.confirming === 'accept' ? i18n.t('mywb.transfer.accept') : i18n.t('mywb.transfer.reject')"
          @click="methods.confirm"
        />
        <wb-button
          class="mt-16"
          size="block"
          variant="white"
          outlined
          data-test-id="transferGoBack"
          :disabled="data.working"
          :label="i18n.t('mywb.transfer.goBack')"
          @click="methods.cancel"
        />
      </template>
    </wb-modal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { cookiePersistance, localPersistance, tokenPersistance } from '@wallbox/http'
import ContainerComponent from '@/components/ContainerComponent.vue'
import ClientImgComponent from '@/components/ClientImgComponent.vue'
import TransferDetails from '@/components/transfer/TransferDetails.vue'
import ENV from '@/engine/env/web.env'
import state from '@/state'
import ROUTES from '@/engine/router/routes'
import { TRANSFER_TOKEN_QUERY } from '@/engine/router/transferToken'
import { useI18n } from '@/hooks/useI18n.hook'
import { AUTH_USE_CASES, TRANSFER_USE_CASES, injectStrict } from '@/engine/injectors'
import {
  TRANSFER_STATUS,
  TransferEmailMismatchError,
  TransferNotActiveError,
  TransferNotFoundError,
  type Transfer
} from '@/core/transfer'

type Action = 'accept' | 'reject'

interface DataType {
  loading: boolean
  loadFailed: boolean
  notFound: boolean
  transfer?: Transfer
  sessionEmail?: string
  wrongAccount: boolean
  confirming?: Action
  working: boolean
  actionError: boolean
  result?: Action
  signingOut: boolean
}

const REDIRECTION_KEY = 'wb-redirection'

const i18n = useI18n()
const route = useRoute()
const router = useRouter()
const authUseCases = injectStrict(AUTH_USE_CASES)
const transferUseCases = injectStrict(TRANSFER_USE_CASES)

const token = route.query[TRANSFER_TOKEN_QUERY]?.toString() ?? ''
const persistance = ENV.api.authSystem === 'cookie' ? cookiePersistance : localPersistance

const data = reactive<DataType>({
  loading: true,
  loadFailed: false,
  notFound: !token,
  wrongAccount: false,
  working: false,
  actionError: false,
  signingOut: false
})

const normalize = (email?: string) => (email ?? '').trim().toLowerCase()

const unavailableKeys = {
  [TRANSFER_STATUS.EXPIRED]: 'mywb.transfer.expired',
  [TRANSFER_STATUS.WITHDRAWN]: 'mywb.transfer.withdrawn',
  [TRANSFER_STATUS.SUPERSEDED]: 'mywb.transfer.superseded',
  [TRANSFER_STATUS.REJECTED]: 'mywb.transfer.alreadyRejected',
  [TRANSFER_STATUS.ACCEPTED]: 'mywb.transfer.alreadyAccepted'
}

const unavailableKey = computed(() => {
  if (data.notFound) return 'mywb.transfer.notFound'
  if (data.result || !data.transfer || data.transfer.status === TRANSFER_STATUS.SENT) return undefined

  return unavailableKeys[data.transfer.status]
})

const current = computed(() => {
  if (data.loading) return 'loading'
  if (data.loadFailed) return 'loadError'
  if (data.result === 'accept') return 'doneAccepted'
  if (data.result === 'reject') return 'doneRejected'
  if (!data.sessionEmail) return 'needsAccount'
  if (data.wrongAccount || normalize(data.sessionEmail) !== normalize(data.transfer?.customerEmail)) {
    return 'wrongAccount'
  }

  return 'ready'
})

const clearTransferRedirection = () => {
  const storage = localStorage.getItem(REDIRECTION_KEY)
  if (!storage) return

  try {
    if (JSON.parse(storage)?.name === ROUTES.TRANSFER) {
      localStorage.removeItem(REDIRECTION_KEY)
    }
  } catch {
    localStorage.removeItem(REDIRECTION_KEY)
  }
}

const methods = {
  async readTransfer () {
    try {
      data.transfer = await transferUseCases.getTransfer(token)
      data.notFound = false
      data.loadFailed = false
    } catch (error) {
      if (error instanceof TransferNotFoundError) {
        data.notFound = true
        data.loadFailed = false
      } else {
        data.loadFailed = true
      }
    }
  },

  async readSession () {
    if (!tokenPersistance(persistance).getToken()) return

    try {
      const me = await authUseCases.getMe()
      data.sessionEmail = me?.email
    } catch {
      data.sessionEmail = undefined
    }
  },

  async load () {
    if (!token) {
      data.loading = false
      return
    }

    data.loading = true
    await Promise.all([methods.readTransfer(), methods.readSession()])
    data.loading = false

    if (data.sessionEmail) clearTransferRedirection()
  },

  goToAuth (name: string) {
    if (!data.transfer) return

    const redirection = { name: ROUTES.TRANSFER, query: { [TRANSFER_TOKEN_QUERY]: token } }
    localStorage.setItem(REDIRECTION_KEY, JSON.stringify(redirection))
    router.push({ name, query: { email: data.transfer.customerEmail } })
  },

  ask (action: Action) {
    if (data.working) return

    data.actionError = false
    data.confirming = action
  },

  cancel () {
    if (data.working) return

    data.confirming = undefined
  },

  async confirm () {
    const action = data.confirming
    if (!action || data.working) return

    data.working = true
    data.actionError = false

    try {
      data.transfer = action === 'accept'
        ? await transferUseCases.acceptTransfer(token)
        : await transferUseCases.rejectTransfer(token)
      data.result = action
      clearTransferRedirection()
    } catch (error) {
      if (error instanceof TransferEmailMismatchError) {
        data.wrongAccount = true
      } else if (error instanceof TransferNotActiveError) {
        await methods.readTransfer()
      } else {
        data.actionError = true
      }
    } finally {
      data.working = false
      data.confirming = undefined
    }
  },

  async signOut () {
    if (data.signingOut) return

    data.signingOut = true
    try {
      await authUseCases.doLogout()
    } catch {
      tokenPersistance(persistance).remove()
    } finally {
      sessionStorage.removeItem('wb-state')
      state.global.removeLocalStorageSensitiveInfo()
      state.user.setInitialState()
      data.sessionEmail = undefined
      data.wrongAccount = false
      data.signingOut = false
    }
  }
}

onMounted(() => {
  methods.load()
})
</script>

<style lang="postcss" scoped>
.wrapper {
  display: grid;
  width: 100vw;
  min-height: 100vh;
  place-items: center;
}

.transfer {
  width: 100%;
  max-width: 480px;
  padding: 48px 24px;
}

.transfer-body {
  width: 100%;
}

.has-centered-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.logo-wallbox {
  width: 64px;
  height: 64px;
}
</style>
